import { queryOptions } from '@tanstack/react-query';
import type { ActionCredential, operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { getApiClientErrorStatus } from '@/services/api/errors';

/**
 * Per-channel action-credential reads (`GET /environments/:id/credentials/:action_type`).
 * The connections overview reads these for the Slack / Twilio inverted guards,
 * the in-app + push + URL-params channel timestamps, and the URL-params enabled
 * flag. A not-configured channel is a fetch error (or a 200 with a null
 * credential) — both resolve to "no credential", never surfacing as an error.
 */

const STALE_TIME = 60_000;

/**
 * Every credential slot the endpoint accepts, off the `{action_type}` path enum
 * the spec publishes (services#24475).
 */
type CredentialActionType =
  operations['get-v1-environments-environment_id-credentials-action_type']['parameters']['path']['action_type'];

/**
 * The channel credential slots this app reads — a subset of the API's, so
 * `Extract` rather than the enum itself: a slot dropped upstream stops being
 * assignable here and surfaces at the call sites that name it, while a new
 * credential type added upstream does not silently join this list.
 */
export type ActionCredentialType = Extract<
  CredentialActionType,
  'slack' | 'twilio' | 'in_app' | 'push_fcm' | 'push_apns' | 'url_params' | 'line' | 'urban_airship'
>;

export const actionCredentialKeys = {
  detail: (workspaceId: number, actionType: ActionCredentialType) =>
    ['workspace', workspaceId, 'action-credential', actionType] as const,
};

/**
 * The resolved credential plus whether the fetch itself succeeded (HTTP 200).
 * Ember distinguishes these: `areURLParamsEnabled` is true purely because the
 * request resolved, while the Slack/Twilio guard keys on the credential *value*
 * being null.
 */
/**
 * How the credential read resolved. Ember's URL-params gate branches on
 * exactly this three-way split: `found` (200) means the feature is enabled,
 * `missing` (the endpoint's 404) means it is disabled, and `error` (any other
 * failure) means the answer is unknown and the page shows a retry affordance.
 */
export type ActionCredentialFetchStatus = 'found' | 'missing' | 'error';

export interface ActionCredentialResult {
  credential: ActionCredential | null;
  /** The request resolved (200) — Ember's `loadURLParamsCredential === true`. */
  present: boolean;
  status: ActionCredentialFetchStatus;
}

/** @public */
export async function getActionCredential(
  workspaceId: number,
  actionType: ActionCredentialType,
): Promise<ActionCredentialResult> {
  try {
    const data = await hydraUiApi.credentials.get({
      params: { path: { environment_id: String(workspaceId), action_type: actionType } },
    });
    return { credential: data.credential ?? null, present: true, status: 'found' };
  } catch (error) {
    const status = getApiClientErrorStatus(error) === 404 ? 'missing' : 'error';
    return { credential: null, present: false, status };
  }
}

/** @public */
export const getActionCredentialQueryOptions = (workspaceId: number, actionType: ActionCredentialType) =>
  queryOptions({
    queryKey: actionCredentialKeys.detail(workspaceId, actionType),
    queryFn: () => getActionCredential(workspaceId, actionType),
    staleTime: STALE_TIME,
  });

/**
 * The same read for a channel's own settings page, which always refetches on
 * entry: the credential can be toggled from other screens (the setup guide, the
 * connections overview), and the page's loading state is part of its contract.
 * Shares the cache slot with the overview read, so a write invalidated there
 * reloads here too. Focus regain and network reconnect do not refetch — the OAuth
 * popup hands focus back to the page, and neither return must look like a fresh
 * page load (the page reports each load as a settings view).
 *
 * @public
 */
export const getChannelCredentialQueryOptions = (workspaceId: number, actionType: ActionCredentialType) =>
  queryOptions({
    ...getActionCredentialQueryOptions(workspaceId, actionType),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
